import './databasesWithPCIData.less'
import {
  ACTION_FETCH_PCI_DATABASES_DATA,
  fetchPCIDatabasesData
} from '../../../complianceDashboardSlice'
import ChartBarSimple from '../../../../../../components/ChartBarSimple/chartBarSimple'
import LoaderView from '../../../../../../components/Loader/loader'
import useWindowSize from '../../../../../../hooks/useWindowResize'
import { getShowLoader } from '../../../../../../reducers/requestReducer'
import { RootState } from '../../../../../../rootReducer'
import { getWindowProps } from '../../../../../../utils'
import {
  UNIQUE_WIDGET_INDEX,
  getUniqueWidgetIndex,
  ChartColors
} from '../../../../mainDashboardv2/constants'
import { AttributeSetData, PCIDatabaseData } from '../../../queries'
import { mockPCIDatabasesData } from '../../mocks'
import WidgetTitle from '../../../../../../components/WidgetTitle'
import Card from '../../../../../accessControl/components/card'
import { getUpdatedUrl } from '../../../../../../utils/urlUtil'
import {
  DATA_SOURCE_TYPE,
  URL_ATTRIBUTES_SET,
  URL_DATA_SOURCES,
  URL_DATABASES,
  URL_INSIGHTS
} from '../../../../../../constants'
import historyService from '../../../../../../services/historyService'
import EmptyStateIcon from '../../../../../../assets/inlineSvg/empty-database-icon.svg'
import { EmptyState } from '../../emptyState/emptyState'
import { hasData } from '../../helper'
import { useDashboardType } from '../../hooks/useDashboardType'
import { DashboardCategory } from '../../../../mainDashboardv2/slice'
import { ComplianceDashboardTypes } from '../../../constants'

import { Typography, Icon, Tooltip } from '@lightbeamai/design-system'
import React, { useEffect, useMemo } from 'react'
import { FormattedNumber, useIntl } from 'react-intl'
import { connect } from 'react-redux'

interface OwnProps {
  useMockData?: boolean
  chartBarAlignment?: 'vertical' | 'horizontal'
  widgetDatasourceId?: string
  widgetDashboardId?: string
  widgetDashboardType?: string
  attributeSetId?: string
}

interface StateProps {
  widget?: PCIDatabaseData | null
  showLoader: boolean
  attributeSetData?: AttributeSetData | null
}

interface DispatchProps {
  fetchPCIDatabasesData: (params: {
    attributeSetId: string
    [UNIQUE_WIDGET_INDEX]?: string
  }) => void
}

type DatabasesWithPCIDataComponentProps = StateProps & DispatchProps & OwnProps

const DatabasesWithPCIData = (props: DatabasesWithPCIDataComponentProps) => {
  const {
    widget,
    showLoader,
    chartBarAlignment = 'horizontal',
    useMockData = false,
    widgetDatasourceId,
    widgetDashboardId,
    widgetDashboardType,
    attributeSetData,
    fetchPCIDatabasesData
  } = props

  const intl = useIntl()

  const { isPciDashboard } = useDashboardType(widgetDashboardType as DashboardCategory)

  const windowProps = getWindowProps(useWindowSize())
  const { isLargeScreen } = windowProps

  const handleSummaryItemClick = () => {
    if (!attributeSetData?.id) return

    historyService.push(
      getUpdatedUrl({
        pathName: `${URL_INSIGHTS}${URL_ATTRIBUTES_SET}/${attributeSetData.id}${URL_DATA_SOURCES}`
      })
    )
  }

  useEffect(() => {
    if (useMockData) return
    if (!attributeSetData?.id) return
    fetchPCIDatabasesData({
      attributeSetId: attributeSetData?.id || '',
      [UNIQUE_WIDGET_INDEX]: getUniqueWidgetIndex(widgetDashboardId, widgetDatasourceId)
    })
  }, [useMockData, fetchPCIDatabasesData, attributeSetData, widgetDashboardId, widgetDatasourceId])

  const chartsData = useMemo(() => {
    const databases = widget?.databaseStats?.databases || []
    const top5Databases = databases.slice(0, 5)

    return top5Databases.map((database) => {
      const maxLength = isLargeScreen ? 12 : 6
      const displayName =
        database.name.length > maxLength
          ? `${database.name.substring(0, maxLength)}...`
          : database.name

      return {
        name: displayName,
        originalName: database.name,
        value: database.columnsCount,
        color: ChartColors.Blue,
        dataKey: database.id,
        datasourceId: database.datasourceId || '',
        datasourceType: database.datasourceType || ''
      }
    })
  }, [widget, isLargeScreen])

  const countsFormatter = (value: number) => intl.formatNumber(value, { notation: 'compact' })

  const handleClick = (val: any) => {
    const databaseId = val?.dataKey
    const datasourceType = val?.datasourceType
    historyService.push(
      getUpdatedUrl({
        pathName: `${URL_DATABASES}/${databaseId}`,
        pageName: URL_DATABASES,
        queryParams: {
          [DATA_SOURCE_TYPE]: datasourceType
        }
      })
    )
  }

  const CustomDatabaseTooltip = (props: any) => {
    const { active, payload } = props

    if (active && payload && payload.length) {
      const data = payload[0].payload

      return (
        <div className="database-widget-custom-tooltip">
          <div className="database-info">
            <Typography variant="baseMinus2" type="strong" as="span">
              {data.originalName || data.name} {/* Show full name in tooltip */}
            </Typography>
            <Typography variant="baseMinus2" type="strong" as="span">
              <FormattedNumber value={data.value} notation="compact" />
            </Typography>
          </div>
          <Typography as="span" variant="baseMinus2">
            {data.datasourceType}
          </Typography>
        </div>
      )
    }
    return null
  }
  const cardTitle = (
    <WidgetTitle
      title={
        <div className="widget-title">
          <Typography color="primary" variant="h6" as="span">
            {intl.formatMessage(
              {
                id: 'compliance.dashboard.pci.widget.databasesWithPCIData.title'
              },
              {
                type: isPciDashboard ? ComplianceDashboardTypes.PCI : ComplianceDashboardTypes.HIPAA
              }
            )}
          </Typography>
          <Tooltip
            title={intl.formatMessage({
              id: isPciDashboard
                ? 'compliance.dashboard.pci.widget.title.tooltip'
                : 'compliance.dashboard.hipaa.widget.title.tooltip'
            })}
            placement="right"
          >
            <Icon size={16} name="InfoCircle" className="pointer" />
          </Tooltip>
        </div>
      }
      bordered
    />
  )

  const hasChartData = hasData(chartsData)

  return (
    <Card className="databases-with-pci-data-widget" cardTitle={cardTitle}>
      <div className={`databases-with-pci-data-chart-wrapper ${chartBarAlignment}`}>
        <div className="dashboard-labels-count-wrapper">
          <div className="label-items">
            <div className="label-item column-tables" onClick={handleSummaryItemClick}>
              <div>
                <Typography color="primary" type="strong" variant="h4">
                  <FormattedNumber
                    value={widget?.databaseStats?.totalColumnsCount || 0}
                    notation="compact"
                  />
                </Typography>
                <Typography color="secondary" type="medium" variant="baseMinus2">
                  {intl.formatMessage({
                    id: 'compliance.dashboard.pci.widget.databasesWithPCIData.summary.columns'
                  })}
                </Typography>
              </div>
              <div>
                <Typography color="primary" type="strong" variant="h4">
                  <FormattedNumber
                    value={widget?.databaseStats?.totalTablesCount || 0}
                    notation="compact"
                  />
                </Typography>
                <Typography color="secondary" type="medium" variant="baseMinus2">
                  {intl.formatMessage({
                    id: 'compliance.dashboard.pci.widget.databasesWithPCIData.summary.tables'
                  })}
                </Typography>
              </div>
            </div>
            <div className="label-item" onClick={handleSummaryItemClick}>
              <Typography color="primary" type="strong" variant="h4">
                <FormattedNumber value={widget?.blobInstancesCount || 0} notation="compact" />
              </Typography>
              <Typography color="secondary" type="medium" variant="baseMinus2">
                {intl.formatMessage({
                  id:
                    'compliance.dashboard.pci.widget.databasesWithPCIData.summary.instancesInBlobs'
                })}
              </Typography>
            </div>
            <div className="label-item" onClick={handleSummaryItemClick}>
              <Typography color="primary" type="strong" variant="h4">
                <FormattedNumber
                  value={widget?.databaseStats?.totalDatabasesCount || 0}
                  notation="compact"
                />
              </Typography>
              <Typography color="secondary" type="medium" variant="baseMinus2">
                {intl.formatMessage(
                  {
                    id: 'compliance.dashboard.pci.widget.databasesWithPCIData.summary.dbsInDS'
                  },
                  {
                    count: widget?.databaseStats?.databases?.length || 0
                  }
                )}
              </Typography>
            </div>
          </div>
        </div>
        {hasChartData ? (
          <div className="charts-wrapper databases-with-pci-data-bar-charts xs-ml-16">
            <Typography as="span" variant="baseMinus2" className="xs-ml-32">
              {intl.formatMessage({
                id: 'compliance.dashboard.pci.widget.databasesWithPCIData.chart.label'
              })}
            </Typography>
            <ChartBarSimple
              className="xs-mt-8 v2-chart"
              data={chartsData}
              cartesianGridHorrizontal
              tickFormatter={{
                fontSize: '0.68rem',
                fill: ChartColors.SecondaryText,
                width: 70,
                textAnchor: 'end'
              }}
              multiColored
              chartMargin={{ left: 20, right: 20, top: 15, bottom: 20 }}
              barSize={60}
              chartResponsiveHeight={isLargeScreen ? 270 : 230}
              {...(isLargeScreen ? { xAxisHeight: 65 } : {})}
              yLabel={intl.formatMessage(
                {
                  id: 'compliance.dashboard.pci.widget.databasesWithPCIData.chart.ylabel'
                },
                {
                  type: isPciDashboard
                    ? ComplianceDashboardTypes.PCI
                    : ComplianceDashboardTypes.HIPAA
                }
              )}
              xAxisAngle={-45}
              YTickFormatter={countsFormatter}
              yAxisLabelPadding={{ dx: -12, dy: -6 }}
              customToolTip={<CustomDatabaseTooltip />}
              tickMargin={10}
              xAxisTickInterval={0}
              xAxisLabelPadding={{ dx: 0, dy: 12 }}
              onBarItemClick={handleClick}
            />
          </div>
        ) : (
          <EmptyState
            title={intl.formatMessage(
              {
                id: 'compliance.dashboard.pci.widget.databasesWithPCIData.errorText'
              },
              {
                type: isPciDashboard ? ComplianceDashboardTypes.PCI : ComplianceDashboardTypes.HIPAA
              }
            )}
            icon={<EmptyStateIcon />}
          />
        )}
      </div>
      <LoaderView size="small" showLoader={showLoader} />
    </Card>
  )
}

const mapStateToProps = (state: RootState, ownProps: OwnProps): StateProps => ({
  widget: ownProps?.useMockData
    ? mockPCIDatabasesData.widget
    : state.complianceDashboard.pciDatabasesData?.[
        getUniqueWidgetIndex(ownProps.widgetDashboardId, ownProps.widgetDatasourceId)
      ],
  attributeSetData: state.complianceDashboard.attributeSetData,
  showLoader: getShowLoader(ACTION_FETCH_PCI_DATABASES_DATA)
})

const mapDispatchToProps: DispatchProps = {
  fetchPCIDatabasesData
}

export default connect(mapStateToProps, mapDispatchToProps)(DatabasesWithPCIData)
